
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, ExternalLink } from "lucide-react";
import { ApprovalPost } from "@/types/approval";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface ApprovalDetailsDialogProps {
  approval: ApprovalPost | null;
  isOpen: boolean;
  onClose: () => void;
  onDelete: (id: string) => void;
}

export function ApprovalDetailsDialog({ 
  approval, 
  isOpen, 
  onClose, 
  onDelete 
}: ApprovalDetailsDialogProps) {
  if (!approval) return null;

  const handleDelete = () => {
    onDelete(approval.id);
    onClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500 text-white';
      case 'pending':
        return 'bg-yellow-500 text-black';
      case 'rejected':
        return 'bg-red-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const renderMedia = () => {
    if (approval.post_type === 'carousel' && approval.carousel_images && approval.carousel_images.length > 0) {
      return (
        <div className="grid grid-cols-2 gap-2 max-h-96 overflow-y-auto">
          {approval.carousel_images.map((image, index) => (
            <img
              key={index}
              src={image}
              alt={`Imagem ${index + 1}`}
              className="w-full h-48 object-cover rounded-lg"
            />
          ))}
        </div>
      );
    } else if (approval.post_type === 'reels' && approval.reels_video) {
      return (
        <video
          src={approval.reels_video}
          controls
          className="w-full max-h-96 rounded-lg"
        >
          Seu navegador não suporta vídeos.
        </video>
      );
    } else if (approval.image_url) {
      return (
        <img
          src={approval.image_url}
          alt={approval.title}
          className="w-full max-h-96 object-cover rounded-lg"
        />
      );
    }
    return (
      <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center">
        <span className="text-gray-500">Nenhuma mídia disponível</span>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{approval.title}</span>
            <Badge className={getStatusColor(approval.status)}>
              {approval.status.toUpperCase()}
            </Badge>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Mídia */}
          <div>
            <h4 className="font-medium mb-2">Mídia</h4>
            {renderMedia()}
          </div>

          {/* Informações */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium mb-1">Cliente</h4>
              <p className="text-sm text-gray-600">{approval.client_name}</p>
            </div>
            <div>
              <h4 className="font-medium mb-1">Plataforma</h4>
              <p className="text-sm text-gray-600">{approval.platform}</p>
            </div>
            <div>
              <h4 className="font-medium mb-1">Tipo</h4>
              <p className="text-sm text-gray-600">{approval.post_type?.toUpperCase() || 'STATIC'}</p>
            </div>
            <div>
              <h4 className="font-medium mb-1">Data de Postagem</h4>
              <p className="text-sm text-gray-600">
                {approval.post_date ? format(new Date(approval.post_date), 'dd/MM/yyyy', { locale: ptBR }) : 'Não definida'}
              </p>
            </div>
          </div>

          {/* Legenda */}
          {approval.caption && (
            <div>
              <h4 className="font-medium mb-2">Legenda</h4>
              <div className="bg-gray-50 p-3 rounded-lg max-h-32 overflow-y-auto">
                <p className="text-sm whitespace-pre-wrap">{approval.caption}</p>
              </div>
            </div>
          )}

          {/* Feedback (se rejeitado) */}
          {approval.status === 'rejected' && approval.feedback && (
            <div>
              <h4 className="font-medium mb-2">Feedback</h4>
              <div className="bg-red-50 p-3 rounded-lg">
                <p className="text-sm text-red-700">{approval.feedback}</p>
              </div>
            </div>
          )}

          {/* Ações */}
          <div className="flex gap-2 pt-4 border-t">
            {approval.shareable_link && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(approval.shareable_link!, '_blank')}
                className="flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Ver Página de Aprovação
              </Button>
            )}
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDelete}
              className="flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Excluir
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
